import React, { FC, useState } from 'react';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import Avatar from 'ui-component/extended/Avatar';
import { ThemeMode } from 'config';

import { dispatch } from 'store';
import { getUsersListStyle1 } from 'store/slices/user';

// assets
import PreviewIcon from '@mui/icons-material/Preview';
import { GetAgencyQuotationsResponse, QuoteStatus, UserRole } from 'Application/models';
import { getBackgroundColor, getUserDataFromStorage, getUserRoleNameFromStorage } from 'Application/utils';
import { Replay } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuoteStore } from 'Application/store';
import { toast } from 'sonner';
import CircularLoader from 'ui-component/CircularLoader';
import { Box } from '@mui/system';

// ==============================|| USER LIST 1 ||============================== //

// table data

function createData(
    quotationnumber: string,
    customername: string,
    email: string,
    plan: string,
    travelperiod: string,
    status: string,
    amount: string
) {
    return { quotationnumber, customername, email, plan, travelperiod, status, amount };
}

export interface QuotesListProps {
    agencyQuotations: GetAgencyQuotationsResponse;
}

const QuotesList: FC<QuotesListProps> = ({ agencyQuotations }) => {
    const theme = useTheme();

    const [isResumeJourneyLoading, setIsResumeJourneyLoading] = useState<boolean>(false);

    const userRole = getUserRoleNameFromStorage();

    const { pathname } = useLocation();
    const pathnameRole = pathname.split('/')[1];

    const { resetQuoteStore, getQuoteDetails, getRecommendedPlans } = useQuoteStore((state) => ({
        resetQuoteStore: state.resetQuoteStore,
        getQuoteDetails: state.getQuoteDetails,
        getRecommendedPlans: state.getRecommendedPlans
    }));

    // const [data, setData] = React.useState([]);
    // const { usersS1 } = useSelector((state) => state.user);

    // React.useEffect(() => {
    //     setData(usersS1);
    // }, [usersS1]);

    const navigate = useNavigate();

    const userRegion = getUserDataFromStorage()!.region;

    const handleResumeQuotationJourney = async (quoteId: string) => {
        toast.info('Resuming quotation journey ...');

        setIsResumeJourneyLoading(true);

        resetQuoteStore();

        //* Get quotation details by quotation number
        await getQuoteDetails(quoteId);

        //* Get recommended plans by quotation number
        await getRecommendedPlans();

        toast.info('Just a minute ...');

        setIsResumeJourneyLoading(false);

        let navigateTo: string = '';

        if (userRole === UserRole.ROLE_ADMINISTRATOR) {
            navigateTo =
                userRegion === 'Kenya' ? '/britam-admin/travel-portal/generate-quote' : '/britam-admin/travel-portal/generate-quote-rw';
        } else if (userRole === UserRole.ROLE_REGIONAL_ADMIN) {
            navigateTo =
                userRegion === 'Kenya' ? '/britam-admin/travel-portal/generate-quote' : '/britam-admin/travel-portal/generate-quote-rw';
        } else if (userRole === UserRole.ROLE_AGENCY_ADMIN) {
            navigateTo = userRegion === 'Kenya' ? '/agency/travel-portal/generate-quote' : '/agency/travel-portal/generate-quote-rw';
        } else if (userRole === UserRole.ROLE_AGENT || userRole === UserRole.ROLE_BRANCH_MANAGER) {
            navigateTo = userRegion === 'Kenya' ? '/agency/travel-portal/generate-quote' : '/agency/travel-portal/generate-quote-rw';
        } else if (userRole === UserRole.ROLE_ISC) {
            navigateTo =
                userRegion === 'Kenya' ? '/britam-isc/travel-portal/generate-quote' : '/britam-isc/travel-portal/generate-quote-rw';
        } else if (userRole === UserRole.ROLE_UNDER_WRITER) {
            navigateTo =
                userRegion === 'Kenya'
                    ? '/britam-underwriter/travel-portal/generate-quote'
                    : '/britam-underwriter/travel-portal/generate-quote-rw';
        } else {
            toast.error('Could not resume quotation journey for this user');
        }

        navigate(navigateTo, { state: { isResumingJourney: true } });
    };

    const handleRedirect = (quoteId: string) => {
        navigate(`/${pathnameRole}/travel-portal/completed-transactions/viewquotation/${quoteId}`);
    };

    React.useEffect(() => {
        dispatch(getUsersListStyle1());
    }, []);

    return isResumeJourneyLoading ? (
        <Box className="h-[60vh]">
            <CircularLoader />
        </Box>
    ) : (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ pl: 3 }}>Quotation No.</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Travel Period</TableCell>
                        <TableCell>Amount (USD)</TableCell>
                        <TableCell>Issued By</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center" sx={{ pr: 3 }}>
                            Actions
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {agencyQuotations.content.length > 0 ? (
                        agencyQuotations.content.map((row, index) => (
                            <TableRow hover key={index}>
                                <TableCell sx={{ pl: 3 }}>{row.quotationNumber}</TableCell>

                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Avatar
                                            alt="User Avatar"
                                            // sx={{
                                            //     backgroundColor: row.customerName
                                            //         ? getBackgroundColor(row.customerName[0])
                                            //         : 'transparent'
                                            // }}
                                            className={`border border-gray-200 ${
                                                row.customerName ? getBackgroundColor(row.customerName[0]) : 'transparent'
                                            }`}
                                        >
                                            {row.customerName[0].trim() === '' ? '?' : row.customerName[0].toUpperCase()}
                                        </Avatar>
                                        <Stack>
                                            <Stack direction="row" alignItems="center" spacing={0.25}>
                                                <Typography variant="subtitle1">
                                                    {row.customerName.trim() === '' ? '--' : row.customerName}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="subtitle2" noWrap>
                                                {row.customerEmailAddress ? row.customerEmailAddress : '--'}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </TableCell>
                                <TableCell>{row.planName ? row.planName : 'Not selected'}</TableCell>
                                <TableCell>{row.departureDate + ' to ' + row.returnDate}</TableCell>
                                <TableCell>{row.totalPremium ? row.totalPremium : '-'}</TableCell>
                                <TableCell>{row.agent ? row.agent : '-'}</TableCell>
                                <TableCell>
                                    {row.quotationStatus === QuoteStatus.FAILED && (
                                        <Chip
                                            label="Failed"
                                            size="small"
                                            sx={{
                                                bgcolor:
                                                    theme.palette.mode === ThemeMode.DARK
                                                        ? 'dark.main'
                                                        : alpha(theme.palette.error.light, 0.6),
                                                color: 'error.dark'
                                            }}
                                        />
                                    )}
                                    {row.quotationStatus === QuoteStatus.CANCELED && (
                                        <Chip
                                            label="Cancelled"
                                            size="small"
                                            sx={{
                                                bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'error.light',
                                                color: 'error.dark'
                                            }}
                                        />
                                    )}
                                    {row.quotationStatus === QuoteStatus.PENDING && (
                                        <Chip
                                            label="Pending"
                                            size="small"
                                            sx={{
                                                bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'warning.light',
                                                color: 'warning.dark'
                                            }}
                                        />
                                    )}
                                    {row.quotationStatus === QuoteStatus.COMPLETED && (
                                        <Chip
                                            label="Completed"
                                            size="small"
                                            sx={{
                                                bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'success.light',
                                                color: 'success.dark'
                                            }}
                                        />
                                    )}
                                    {row.quotationStatus === QuoteStatus.DRAFT && (
                                        <Chip
                                            label="Draft"
                                            size="small"
                                            sx={{
                                                bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'info.light',
                                                color: 'info.dark'
                                            }}
                                        />
                                    )}
                                    {row.quotationStatus === QuoteStatus.EXPIRED && (
                                        <Chip
                                            label="Expired"
                                            size="small"
                                            sx={{
                                                bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'error.light',
                                                color: 'error.dark'
                                            }}
                                        />
                                    )}
                                    {row.quotationStatus === QuoteStatus.DELETED && (
                                        <Chip
                                            label="Deleted"
                                            size="small"
                                            sx={{
                                                bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'error.light',
                                                color: 'error.dark'
                                            }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="center" sx={{ pr: 3 }}>
                                    <Stack direction="row" justifyContent="end" alignItems="center">
                                        {(row.quotationStatus === QuoteStatus.COMPLETED || row.quotationStatus === QuoteStatus.PENDING) && (
                                            <Tooltip placement="top" title="View Quote">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleRedirect(row.quoteId)} // Pass the policyNumber here
                                                    size="large"
                                                >
                                                    <PreviewIcon sx={{ fontSize: '1.1rem', ml: 1 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {!(
                                            row.quotationStatus === QuoteStatus.COMPLETED ||
                                            row.quotationStatus === QuoteStatus.EXPIRED ||
                                            row.quotationStatus === QuoteStatus.CANCELED
                                        ) ? (
                                            <Tooltip placement="top" title="Resume journey">
                                                <IconButton
                                                    color="secondary"
                                                    sx={{
                                                        color: 'green.dark',
                                                        borderColor: 'green.main',
                                                        '&:hover ': { bgcolor: 'green.light' }
                                                    }}
                                                    size="large"
                                                    onClick={() => handleResumeQuotationJourney(row.quoteId)}
                                                >
                                                    <Replay sx={{ fontSize: '1.1rem' }} />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <IconButton
                                                disabled
                                                color="secondary"
                                                sx={{
                                                    color: 'green.dark',
                                                    borderColor: 'green.main',
                                                    '&:hover ': {
                                                        bgcolor: 'green.light',
                                                        cursor: 'pointer'
                                                    }
                                                }}
                                                size="large"
                                                onClick={() => handleResumeQuotationJourney(row.quoteId)}
                                            >
                                                <Replay sx={{ fontSize: '1.1rem' }} />
                                            </IconButton>
                                        )}
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={6}>
                                <Typography variant="subtitle1" align="center">
                                    No quotations available
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default QuotesList;
